/**
 * THE ANNUAL BREAKDOWN: one row per calendar year, from a finished run's trade list.
 *
 * <h2>This is a deliberate SECOND implementation, and that is the risk it carries</h2>
 *
 * The totals already come from the backend's {@code LedgerStatistics}. Nothing over the wire
 * carries a per-year split, so grouping has to happen here — and a second implementation of a
 * metric is how a UI comes to disagree with the report it sits next to. This repository has paid
 * for that once already, which is why {@code LiveAccount} prices closed trades with the same
 * {@code AccountSimulation.priceAt} the final report uses rather than accumulating its own.
 *
 * <p>The protection is the same one: {@link annualBreakdown} returns a TOTAL row computed by this
 * code over every trade, and {@code annualBreakdown.test.js} asserts that row equals the
 * backend's own `statistics` to within floating-point noise. If the two ever diverge, that test
 * fails rather than a pitch deck being wrong.
 *
 * <h2>Each arithmetic choice mirrors LedgerStatistics exactly</h2>
 *
 * <pre>
 *   win            pnl &gt; 0 STRICTLY — a scratch is a loss, as TradeRecord.isWin has it
 *   net PnL        COMPOUNDED, equity *= 1 + pnl/100. Summing is wrong by construction:
 *                  +10% then -10% is a 1% LOSS and sums to zero
 *   profit factor  gross gain / gross loss, both SUMS of percent — and NULL when nothing lost,
 *                  because an absent denominator is not a perfect score
 *   max drawdown   deepest peak-to-trough fall of the TRADE-SEQUENCE equity curve, so the list
 *                  must stay in the order the trades were taken
 * </pre>
 *
 * <h2>Each year's curve restarts at 1.0</h2>
 *
 * A year's "net PnL" is that year's own compounded return, and its drawdown is measured from its
 * own peak. The alternative — carrying one continuous curve across the whole run — would make
 * every year after a drawdown report the inherited one, and a 2009 row would describe 2008.
 */

/** The year of an ISO timestamp, by STRING SLICE. */
const yearOf = (iso) => {
  // Sliced, never parsed through Date. Exit times are offset date-times in the exchange's own
  // offset, and `new Date(iso).getFullYear()` converts through the READER's timezone — which
  // moves a 2020-01-01T09:30-05:00 exit into 2019 for anyone east of UTC. This repository has
  // already been bitten by exactly that in the exit-date-to-candle mapping.
  if (typeof iso !== 'string' || iso.length < 4) return null;
  const year = iso.slice(0, 4);
  return /^\d{4}$/.test(year) ? year : null;
};

/** A fresh, empty book. */
const emptyBook = () => ({
  trades: 0, wins: 0, grossGain: 0, grossLoss: 0, equity: 1, peak: 1, maxDrawdown: 0,
  // NULL until a priced trade arrives, which is how an UNSIZED run stays distinguishable from a
  // sized one that happened to break even. A zero in a currency column is a result; this is the
  // absence of one.
  netPnl: null,
});

/**
 * Rolls one trade into a book, exactly as LedgerStatistics does.
 *
 * <p>`net` is the trade's own dollar result, or null on an unsized run. SUMMED, where the
 * percentage is COMPOUNDED -- and that is not an inconsistency: each `netPnl` was already sized
 * against the equity standing when the trade was taken, so the compounding is inside each figure
 * and `startingEquity + sum(netPnl)` is the compounded result. Compounding them again would
 * count it twice. The same reasoning `portfolioMetrics` records.
 */
const accumulate = (book, pnl, net) => {
  book.trades += 1;
  if (Number.isFinite(net)) {
    book.netPnl = (book.netPnl === null ? 0 : book.netPnl) + net;
  }
  if (pnl > 0) {
    book.wins += 1;
    book.grossGain += pnl;
  } else {
    book.grossLoss += -pnl;
  }
  book.equity *= 1 + pnl / 100;
  book.peak = Math.max(book.peak, book.equity);
  if (book.peak > 0) {
    book.maxDrawdown = Math.max(book.maxDrawdown, ((book.peak - book.equity) / book.peak) * 100);
  }
};

/** A finished book as a row. */
const toRow = (label, book) => ({
  year: label,
  trades: book.trades,
  wins: book.wins,
  losses: book.trades - book.wins,
  winRatePercent: book.trades > 0 ? (100 * book.wins) / book.trades : 0,
  // NULL, never a big number. The UI renders "n/a".
  profitFactor: book.grossLoss > 0 ? book.grossGain / book.grossLoss : null,
  // THE COMPOUNDED GROSS RETURN, which is what `pnlPercent` is on every trade in this codebase:
  // the unsized percentage. Named `netPnlPercent` because `LedgerStatistics` names its own
  // equivalent that, and a second name for one quantity is how two panels come to disagree.
  netPnlPercent: (book.equity - 1) * 100,
  // AND THE DOLLARS, when the run was sized. Null otherwise -- the two columns answer different
  // questions and a reader comparing years wants both: a year can be up on percentage and down
  // on dollars if its trades were sized against a smaller book.
  netPnl: book.netPnl,
  maxDrawdownPercent: book.maxDrawdown,
});

/**
 * One row per calendar year of EXIT, oldest first, plus an all-trades total.
 *
 * @param trades the run's completed trades, in the order they were taken
 * @returns {{rows: Array, total: Object}} — `total` is computed by this same code over every
 *          trade, so it is directly comparable with the backend's `statistics` and is what the
 *          test pins the two implementations together with
 */
export function annualBreakdown(trades) {
  const list = Array.isArray(trades) ? trades : [];
  const byYear = new Map();
  const all = emptyBook();

  list.forEach((entry) => {
    // STAMPED OR BARE. The replay timeline ships `{frameIndex, beforeWindow, trade}` and the
    // research dialog ships the trade itself; accepting both here beats a caller remembering to
    // unwrap, which is the kind of thing one of two callers forgets.
    const t = entry && entry.trade ? entry.trade : entry;
    // OPEN POSITIONS ARE EXCLUDED, as they are from every figure in the report beside this one.
    // An open trade's mark is not a result, and mixing marks into realised statistics is how a
    // losing run reports flat because its worst trade had not closed.
    if (!t || t.open) return;
    const year = yearOf(t.exitTime);
    const pnl = Number.isFinite(t.pnlPercent) ? t.pnlPercent : 0;
    const net = Number.isFinite(t.netPnl) ? t.netPnl : null;
    if (year !== null) {
      if (!byYear.has(year)) byYear.set(year, emptyBook());
      accumulate(byYear.get(year), pnl, net);
    }
    // The TOTAL counts every closed trade, including any whose exit time could not be read --
    // otherwise it would silently stop matching the backend, which is the one thing it exists
    // to be checkable against.
    accumulate(all, pnl, net);
  });

  return {
    rows: [...byYear.keys()].sort().map((y) => toRow(y, byYear.get(y))),
    total: toRow('All', all),
  };
}

const pf = (value) => (value === null ? 'n/a' : value.toFixed(2));
const pc = (value, places = 2) => `${value >= 0 ? '+' : ''}${value.toFixed(places)}%`;

/**
 * The breakdown as a Markdown table, column-aligned so it reads as a table in plain text too.
 *
 * <p>Padded rather than left ragged. Markdown renders either, but the string is also going to be
 * looked at in a diff, a chat window and a terminal before it reaches a slide, and a ragged
 * table is unreadable in all three.
 *
 * @param label a caption line above the table — the symbol and provider the run was for. Without
 *        it a table pasted into a deck is a column of numbers about nothing in particular
 */
export function annualMarkdown({ rows, total }, label = '') {
  const header = ['Year', 'Trades', 'Wins', 'Losses', 'Win Rate', 'Profit Factor',
    'Net PnL', 'Max DD'];
  const body = [
    ...rows.map((r) => [r.year, String(r.trades), String(r.wins), String(r.losses),
      `${r.winRatePercent.toFixed(1)}%`, pf(r.profitFactor),
      pc(r.netPnlPercent), `${r.maxDrawdownPercent.toFixed(2)}%`]),
    ['**All**', `**${total.trades}**`, `**${total.wins}**`, `**${total.losses}**`,
      `**${total.winRatePercent.toFixed(1)}%**`, `**${pf(total.profitFactor)}**`,
      `**${pc(total.netPnlPercent)}**`, `**${total.maxDrawdownPercent.toFixed(2)}%**`],
    // THE ROW THE USER FILLS IN. Blank cells rather than zeros: a zero is a claim that the
    // manual book made nothing, and this row exists precisely because nobody here knows what it
    // made. Left as the last row so the comparison reads engine-then-human.
    ['Discretionary / Manual Execution', '', '', '', '', '', '', ''],
  ];

  const widths = header.map((h, i) => Math.max(h.length,
    ...body.map((row) => row[i].length)));
  const line = (cells) => `| ${cells.map((c, i) => c.padEnd(widths[i])).join(' | ')} |`;
  const rule = `|${widths.map((w) => '-'.repeat(w + 2)).join('|')}|`;

  return [
    ...(label ? [label, ''] : []),
    line(header),
    rule,
    ...body.map(line),
  ].join('\n');
}
